import { Client, Account, Storage, ID, Query } from 'appwrite';

// Appwrite configuration
const client = new Client()
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1')
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

// Create service instances
const account = new Account(client);
const storage = new Storage(client);

// Database and Table IDs
const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID || '691e06cc001f0a51d654';

// Table IDs (Appwrite TablesDB requires table IDs, not names)
const USERS_TABLE_ID = '691f68bd0002e1f35a54'; // users table
const STORE_TABLE_ID = '691f73c6000bfc037aa8'; // store table (singular, not stores)
const PAYMENT_METHOD_TABLE_ID = '691f73970021791b3cdf'; // paymentMethod table
const PRODUCTS_TABLE_ID = '691f6a6c001ae183e138'; // products table
const ORDERS_TABLE_ID = '691f6c47003878772771'; // orders table
const CART_TABLE_ID = '691f71c9002ffd69ef22'; // cart table
const VEHICLE_TYPES_TABLE_ID = 'vehicleTypes';
const DELIVERY_SETTINGS_TABLE_ID = 'deliverySettings';
const DELIVERY_RATINGS_TABLE_ID = 'deliveryRatings';

// Legacy constants for backward compatibility (will be removed in future)
const USERS_TABLE = USERS_TABLE_ID;
const STORES_TABLE = STORE_TABLE_ID; // Note: actual table is 'store', not 'stores'
const STORE_LOCATIONS_TABLE = null; // This table doesn't exist - location data is in store table

// TablesDB REST API helper
// Session is sent via credentials: 'include' (cookies). No pre-verification—
// the request succeeds or fails on the server. This avoids 2 extra API calls per request.
const getTablesDBHeaders = async (requireSession = true) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID;
  if (projectId) {
    headers['X-Appwrite-Project'] = projectId;
  }
  return headers;
};

const getTablesDBBaseUrl = () => {
  const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1';
  // TablesDB endpoint: /tablesdb/{databaseId}/tables/{tableId}/rows
  return `${endpoint}/tablesdb/${DATABASE_ID}`;
};

// TablesDB API wrapper
const tablesDB = {
  // Create a row
  createRow: async (databaseId, tableId, rowId, data, permissions = null, requireSession = true) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows`;
    
    // Build request body
    const body = {
      rowId: rowId,
      data: data
    };
    
    // DO NOT pass permissions - let table-level permissions handle access control
    // Row-level permissions in Appwrite TablesDB have strict format requirements
    // and passing "any" or invalid formats causes errors
    // Table-level permissions (set in Appwrite console) will handle access control
    
    // Get headers (async to check session)
    // requireSession = false for user registration (Users table has create("any") permission)
    const headers = await getTablesDBHeaders(requireSession);
    
    // Debug: Log request details
    // console.log('📤 TablesDB Create Request:', {
    //   url,
    //   method: 'POST',
    //   hasSession: true, // We verified session in getTablesDBHeaders
    //   credentials: 'include'
    // });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      credentials: 'include', // Include cookies for session - CRITICAL for client-side auth
      mode: 'cors', // Explicitly set CORS mode
      body: JSON.stringify(body)
    });
    
    // Debug: Log response details
    // console.log('📥 TablesDB Response:', {
    //   status: response.status,
    //   statusText: response.statusText,
    //   ok: response.ok,
    //   headers: Object.fromEntries(response.headers.entries())
    // });
    
    if (!response.ok) {
      let errorMessage = `Failed to create row (${response.status})`;
      let errorData = null;
      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.error('❌ TablesDB Error:', {
          message: errorData.message,
          code: errorData.code,
          type: errorData.type,
          version: errorData.version
        });
      } catch (e) {
        const text = await response.text();
        console.error('❌ TablesDB Error Response (text):', text);
        errorMessage = text || errorMessage;
      }
      
      // Create error object with type for better handling
      const error = new Error(errorMessage);
      if (errorData) {
        error.type = errorData.type;
        error.code = errorData.code;
      }
      throw error;
    }
    
    return response.json();
  },

  // Get a row
  getRow: async (databaseId, tableId, rowId) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    
    // console.log(`📥 TablesDB Get Request: ${url}`);
    // console.log(`   Row ID: ${rowId}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      credentials: 'include'
    });
    
    // console.log(`📥 TablesDB Get Response: ${response.status} ${response.statusText}`);
    
    if (!response.ok) {
      let errorMessage = `Failed to get row (${response.status})`;
      let errorData = null;
      
      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        if (response.status !== 404) {
          console.error(`❌ TablesDB Get Error:`, { status: response.status, error: errorData, rowId });
        }
      } catch (e) {
        const text = await response.text();
        errorMessage = text || errorMessage;
        if (response.status !== 404) {
          console.error(`❌ TablesDB Get Error (non-JSON):`, { status: response.status, text, url });
        }
      }
      
      // Create error with type and code for better handling
      const error = new Error(errorMessage);
      if (errorData) {
        error.type = errorData.type;
        error.code = errorData.code;
      }
      throw error;
    }
    
    const data = await response.json();
    // console.log(`✅ TablesDB Get Success:`, { rowId: data.$id });
    return data;
  },

  // List rows
  listRows: async (databaseId, tableId, queries = []) => {
    // Convert Query objects to strings if needed
    const queryStrings = queries.map(q => {
      if (typeof q === 'string') {
        return q;
      }
      // If it's a Query object, convert to string
      if (q && typeof q.toString === 'function') {
        return q.toString();
      }
      return String(q);
    });
    
    const queryParams = queryStrings.length > 0 
      ? '?' + queryStrings.map(q => `queries[]=${encodeURIComponent(q)}`).join('&')
      : '';
    
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows${queryParams}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      credentials: 'include'
    });
    
    if (!response.ok) {
      let errorMessage = `Failed to list rows (${response.status})`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch (e) {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  },

  // Update a row
  updateRow: async (databaseId, tableId, rowId, data) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify({
        data: data
      })
    });
    
    if (!response.ok) {
      let errorMessage = `Failed to update row (${response.status})`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch (e) {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  },

  // Decrement a numeric column atomically, floored at `min` server-side.
  // A get-then-update from the client can lose a unit when two writes land
  // close together; this does the read, subtract and floor in one step.
  decrementRowColumn: async (databaseId, tableId, rowId, column, value = 1, min = null) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}/${column}/decrement`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, {
      method: 'PATCH',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify({ value, min })
    });

    if (!response.ok) {
      let errorMessage = `Failed to decrement ${column} (${response.status})`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch (e) {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  // Delete a row
  deleteRow: async (databaseId, tableId, rowId) => {
    const url = `${getTablesDBBaseUrl()}/tables/${tableId}/rows/${rowId}`;
    const headers = await getTablesDBHeaders();
    const response = await fetch(url, {
      method: 'DELETE',
      headers: headers,
      credentials: 'include'
    });
    
    if (!response.ok) {
      let errorMessage = `Failed to delete row (${response.status})`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch (e) {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  }
};

// Helper function to get file view URL
const getFileViewUrl = (fileId) => {
  const bucketId = process.env.REACT_APP_APPWRITE_STORAGE_BUCKET_ID;
  const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID;
  const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.beetletz.online/v1';
  return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/view?project=${projectId}`;
};

// Function to upload file to Appwrite storage
// Note: Session verification should be done in the calling component before calling this function
const uploadBusinessLicense = async (file) => {
  try {
    // Validate file type and size
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload PDF, JPEG, or PNG.');
    }

    if (file.size > maxSize) {
      throw new Error('File is too large. Maximum size is 5MB.');
    }

    // Generate a unique file name
    const fileName = `business_license_${Date.now()}_${file.name}`;

    // Upload file to Appwrite storage
    const uploadedFile = await storage.createFile(
      process.env.REACT_APP_APPWRITE_STORAGE_BUCKET_ID, 
      ID.unique(), 
      file
    );

    // Construct file view URL
    const fileUrl = getFileViewUrl(uploadedFile.$id);

    return {
      fileId: uploadedFile.$id,
      fileUrl: fileUrl,
      fileName: fileName
    };
  } catch (error) {
    console.error('Appwrite file upload error:', error);
    
    // Provide more specific error messages
    if (error.message.includes('Invalid file type')) {
      throw new Error('Invalid file type. Only PDF, JPEG, and PNG are allowed.');
    }
    
    if (error.message.includes('File is too large')) {
      throw new Error('File size exceeds 5MB limit.');
    }
    
    // Log the full error for debugging
    console.error('Full Appwrite upload error:', error);
    
    throw new Error('Failed to upload business license. Please check your connection and try again.');
  }
};

// Export all services and constants
export {
  client,
  account,
  storage,
  tablesDB, 
  uploadBusinessLicense, 
  getFileViewUrl,
  DATABASE_ID,
  USERS_TABLE,
  USERS_TABLE_ID,
  STORES_TABLE,
  STORE_TABLE_ID,
  PAYMENT_METHOD_TABLE_ID,
  PRODUCTS_TABLE_ID,
  ORDERS_TABLE_ID,
  CART_TABLE_ID,
  VEHICLE_TYPES_TABLE_ID,
  DELIVERY_SETTINGS_TABLE_ID,
  DELIVERY_RATINGS_TABLE_ID,
  STORE_LOCATIONS_TABLE,
  Query,
  ID
};

const appwriteConfig = { 
  client, 
  account, 
  storage, 
  tablesDB, 
  uploadBusinessLicense, 
  getFileViewUrl,
  DATABASE_ID,
  USERS_TABLE,
  USERS_TABLE_ID,
  STORES_TABLE,
  STORE_TABLE_ID,
  PAYMENT_METHOD_TABLE_ID,
  PRODUCTS_TABLE_ID,
  ORDERS_TABLE_ID,
  CART_TABLE_ID,
  STORE_LOCATIONS_TABLE,
  Query,
  ID
}; 

export default appwriteConfig; 